import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAgents, useMyDIDs } from '../../api/hooks';
import { upi, type SponsorBanks } from '../../api/upi';
import MandatesTab from './MandatesTab';
import PaymentsTab from './PaymentsTab';
import InstrumentsTab from './InstrumentsTab';

type TabKey = 'mandates' | 'payments' | 'instruments';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'mandates', label: 'Mandates', icon: 'contract' },
  { key: 'payments', label: 'Payments', icon: 'payments' },
  { key: 'instruments', label: 'Bank instruments', icon: 'account_balance' },
];

/**
 * IDA-UPI for customers and agent operators: the mandates given to agents,
 * the payments made under them, and the bank instruments they draw on.
 */
export default function UPIPayments() {
  const [params, setParams] = useSearchParams();
  const { data: dids, loading: didsLoading } = useMyDIDs();
  const { data: agents, loading: agentsLoading } = useAgents();
  const [banks, setBanks] = useState<SponsorBanks | null>(null);
  const [tab, setTab] = useState<TabKey>((params.get('tab') as TabKey) || 'mandates');

  useEffect(() => { upi.sponsorBanks().then(setBanks).catch(() => setBanks(null)); }, []);

  const identities = useMemo(() => [
    // An agent's own DID is listed among the user's DIDs too; it is not a customer.
    ...(dids ?? []).filter((d) => d.status === 'active' && !d.did.startsWith('did:adi:agent:'))
      .map((d) => ({ value: `customer|${d.did}`, label: `Me · ${d.did}` })),
    ...(agents ?? []).filter((a) => a.status === 'active').map((a) => ({ value: `agent|${a.did}`, label: `My agent · ${a.did}` })),
  ], [dids, agents]);

  const fromURL = params.get('agent') ? `agent|${params.get('agent')}` : params.get('customer') ? `customer|${params.get('customer')}` : '';
  const selected = fromURL || identities[0]?.value || '';
  const [role, did] = selected.split('|') as ['customer' | 'agent', string];

  const choose = (v: string) => {
    const [r, d] = v.split('|');
    setParams({ [r]: d, tab });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">UPI payments</h1>
        <p className="mt-1 text-sm text-text-muted">
          Mandates you give agents to pay over UPI, the payments made under them, and the bank instruments they draw on.
          Your bank authorises every payment; IDA checks it against your mandate first.
        </p>
      </div>

      {banks?.mockBank && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          This server uses the mock sponsor bank ({banks.mockBank.slice(0, 28)}…). It is for testing and moves no money.
        </div>
      )}
      {banks && !banks.paymentsEnabled && (
        <div className="rounded-lg border border-border-subtle bg-slate-50 p-3 text-xs text-text-muted">
          Payments are not switched on for this server, so no payment can be sent to a bank. Mandates can still be set up.
        </div>
      )}

      <label className="block max-w-2xl">
        <span className="text-xs font-semibold text-text-muted">Showing</span>
        <select id="upi-identity" value={selected} onChange={(e) => choose(e.target.value)} className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs">
          {fromURL && !identities.some((i) => i.value === fromURL) && <option value={fromURL}>{fromURL.replace('|', ' · ')}</option>}
          {identities.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
        </select>
      </label>

      <div className="flex gap-6 border-b border-border-subtle">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); if (did) setParams({ [role]: did, tab: t.key }); }}
            className={`flex items-center gap-1.5 border-b-2 pb-2 pt-1 text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === t.key ? 'border-primary text-primary' : 'border-transparent text-text-faint hover:text-text-muted'
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {!did && <p className="text-sm text-text-muted">{didsLoading || agentsLoading ? 'Loading…' : 'Create a DID or register an agent first.'}</p>}
      {did && tab === 'mandates' && <MandatesTab key={selected} role={role} did={did} />}
      {did && tab === 'payments' && <PaymentsTab key={selected} role={role} did={did} mockBank={!!banks?.mockBank} />}
      {did && tab === 'instruments' && <InstrumentsTab key={selected} role={role} did={did} mockBank={banks?.mockBank} />}
    </div>
  );
}
